<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid profile-info-container">
        <v-card class="p-b-40 p-t-30 p-l-30 p-r-30 m-b-40">
          <div class="el-loading-mask" v-if="loading">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>
          <!-- Profile Name -->
          <div class="row">
            <div class="col-md-12 header">
              <div class="overview-wrap">
                <h3 class="title-7">
                  <v-btn
                    text icon
                    class="m-0"
                    @click="$router.back()">
                    <i class="zmdi zmdi-long-arrow-left"></i>
                  </v-btn>
                  {{ profile.name }}
                  <span class="p-l-20 status--gray" style="font-weight: normal">ID={{ profile.id }}</span>
                </h3>
                <div class="btn-actions-pane-right">
                  <v-btn color="primary" @click="editProfile()">
                    {{ text('edit_btn') }}
                  </v-btn>
                  <v-btn color="error" @click="profileRemoveDialog = true">
                    {{ text('delete_btn') }}
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
          <!-- Details -->
          <div class="row">
            <div class="col-md-12">
              <!-- Audio -->
              <table class="m-t-30">
                <tbody>
                <tr>
                  <td class="column-title">
                    {{ text('audio') }}
                  </td>
                  <td class="text-uppercase codec">
                    {{ profile.audio_template.codec }}
                  </td>
                </tr>
                <tr>
                  <td>{{ text('channels') }}</td>
                  <td>
                    {{ audioChannelsMap[profile.audio_template.channels] || 'Same as source' }}
                    <audio-channels-tooltip/>
                  </td>
                </tr>
                <tr>
                  <td>{{ text('bitrate') }}</td>
                  <td>{{ profile.audio_template.bitrate }} Kbps</td>
                </tr>
                <tr>
                  <td>{{ text('sample_rate') }}</td>
                  <td>
                    <span v-if="profile.audio_template.sample_rate">
                      {{ profile.audio_template.sample_rate }} Hz
                    </span>
                    <span v-else>Same as source</span>
                  </td>
                </tr>
                </tbody>
              </table>
              <!-- Video -->
              <table class="m-t-50">
                <tbody>
                <tr>
                  <td class="column-title">
                    {{ text('video') }}
                  </td>
                  <td class="text-uppercase codec">
                    <span v-if="profile.video_template.codec">
                      {{ videoCodecMap[profile.video_template.codec][0] }}
                    </span>
                    <span v-else>N/A</span>
                  </td>
                </tr>
                <tr>
                  <td>{{ text('encoder') }}</td>
                  <td>
                    <span v-if="profile.video_template.codec">
                      {{ videoCodecMap[profile.video_template.codec][1]
                      || videoCodecMap[profile.video_template.codec][2] }}
                    </span>
                    <span v-else>N/A</span>
                  </td>
                </tr>
                <tr v-if="profile.video_template.streams.length" class="streams-line">
                  <td>{{ text('streams') }}</td>
                  <td>
                    <table class="profile-streams m-t-10">
                      <thead>
                      <tr>
                        <th></th>
                        <th v-if="profile.video_template.codec.startsWith('h264')">
                          {{ text('profile') }} <h264-profile-tooltip/>
                        </th>
                        <th v-if="profile.video_template.codec.startsWith('h265')">
                          {{ text('profile') }} <h265-profile-tooltip/>
                        </th>
                        <th v-if="profile.video_template.codec.startsWith('h26')">
                          {{ text('level') }} <h26x-level-tooltip/>
                        </th>
                        <th>{{ text('resolution') }}</th>
                        <th>{{ text('bitrate_kbps') }}</th>
                        <th>{{ text('resize_mode') }} <resize-mode-tooltip/></th>
                        <th>{{ text('frame_rate') }}</th>
                        <th>
                          KeyInt <keyframe-interval-tooltip/>
                        </th>
                        <th v-if="currentProject === 'file-transcoding'">
                          MinKeyInt <min-keyframe-interval-tooltip/>
                        </th>
                        <th v-if="profile.video_template.codec.startsWith('h26')">
                          {{ text('ref_frames') }} <reference-frames-tooltip/>
                        </th>
                        <th v-if="profile.video_template.codec.startsWith('h26')">
                          BFrames <b-frames-tooltip/>
                        </th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(stream, i) of profile.video_template.streams" :key="i">
                        <td class="text-left" style="width: 60px;"># {{ i+1 }}</td>
                        <td v-if="profile.video_template.codec.startsWith('h264')">
                          {{ h264ProfileMap[stream.h264_codec_profile] }}
                        </td>
                        <td v-if="profile.video_template.codec.startsWith('h265')">
                          {{ h265ProfileMap[stream.h264_codec_profile] }}
                        </td>
                        <td v-if="profile.video_template.codec.startsWith('h26')">
                          {{ stream.h264_codec_level || 'Auto' }}
                        </td>
                        <td>{{ stream.resolution || 'Same as source' }}</td>
                        <td>{{ stream.bitrate }}</td>
                        <td>{{ resizeModeMap[stream.aspect_ratio] || 'Same as source' }}</td>
                        <td>
                          <span v-if="stream.target_fps">{{ stream.target_fps }} fps</span>
                          <span v-else>Same as source</span>
                        </td>
                        <td>
                          {{ stream.keyframe_int || '0' }}
                        </td>
                        <td v-if="currentProject === 'file-transcoding'">
                          {{ stream.min_keyframe_int || '0' }}
                        </td>
                        <td v-if="profile.video_template.codec.startsWith('h26')">
                          {{ stream.h264_reference_frames || '0'}}
                        </td>
                        <td v-if="profile.video_template.codec.startsWith('h26')">
                          {{ stream.h264_bframes || '0' }}
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-card>
        <!-- Profile Remove Dialog -->
        <v-dialog v-model="profileRemoveDialog" max-width="500" persistent>
          <v-card class="pa-2">
            <v-card-title class="headline">
              {{ text('delete_btn') }} {{ profile.name }}?
            </v-card-title>
            <v-card-text>
              <div class="row">
                <div class="col-md-1">
                  <i class="zmdi zmdi-alert-triangle warning--text"></i>
                </div>
                <div class="col-md-11">
                  <strong>{{ text('warning') }}:</strong> {{ text('delete_profile_warning') }}
                </div>
              </div>
              <div class="row p-t-30 p-b-20">
                <div class="col-md-12">
                  {{ text('proceed') }}
                  <v-form v-model="profileDelete.isFormValid">
                    <v-text-field
                      :append-icon="profileDelete.isFormValid ? 'zmdi zmdi-check' : 'zmdi zmdi-close'"
                      :rules="profileDeleteRules"
                      type="text"
                      :label="text('profile_name_enter')"></v-text-field>
                  </v-form>
                </div>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                depressed small
                color="primary"
                @click="profileRemoveDialog = false">
                {{ text('no_btn') }}
              </v-btn>
              <v-btn
                depressed small
                color="error"
                @click="deleteProfile()"
                :disabled="!profileDelete.isFormValid">
                {{ text('yes_btn') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>
  </div>
</template>

<script>
import AudioChannelsTooltip from '@/components/transcoding/profile/tooltips/AudioChannelsTooltip.vue';
import ResizeModeTooltip from '@/components/transcoding/profile/tooltips/ResizeModeTooltip.vue';
import KeyframeIntervalTooltip from '@/components/transcoding/profile/tooltips/KeyframeIntervalTooltip.vue';
import MinKeyframeIntervalTooltip from '@/components/transcoding/profile/tooltips/MinKeyframeIntervalTooltip.vue';
import H264ProfileTooltip from '@/components/transcoding/profile/tooltips/H264ProfileTooltip.vue';
import H265ProfileTooltip from '@/components/transcoding/profile/tooltips/H265ProfileTooltip.vue';
import H26xLevelTooltip from '@/components/transcoding/profile/tooltips/H26xLevelTooltip.vue';
import ReferenceFramesTooltip from '@/components/transcoding/profile/tooltips/ReferenceFramesTooltip.vue';
import BFramesTooltip from '@/components/transcoding/profile/tooltips/BFramesTooltip.vue';
import {
  AUDIO_CHANNELS_MAP, H264_CODEC_PROFILE_MAP, H265_CODEC_PROFILE_MAP, VIDEO_CODEC_MAP,
  RESIZE_MODE_MAP,
} from '../shared/types';

export default {
  components: {
    AudioChannelsTooltip,
    ResizeModeTooltip,
    KeyframeIntervalTooltip,
    MinKeyframeIntervalTooltip,
    H264ProfileTooltip,
    H265ProfileTooltip,
    H26xLevelTooltip,
    ReferenceFramesTooltip,
    BFramesTooltip,
  },
  created() {
    const profileId = this.$route.params.id;
    if (this.currentProject === 'live-transcoding') {
      this.routerPrefix = 'Live';
    }
    this.$store.commit('setPageLoader');
    this.$store.dispatch('encodingProfiles/getProfile', profileId)
      .catch(() => {
        this.$router.push({ name: `${this.routerPrefix}EncodingProfileListPage` });
      })
      .finally(() => this.$store.commit('resolvePageLoader'));
  },
  data() {
    return {
      profileRemoveDialog: false,
      currentTab: 'general',
      audioChannelsMap: AUDIO_CHANNELS_MAP,
      videoCodecMap: VIDEO_CODEC_MAP,
      h264ProfileMap: H264_CODEC_PROFILE_MAP,
      h265ProfileMap: H265_CODEC_PROFILE_MAP,
      resizeModeMap: RESIZE_MODE_MAP,
      profileDelete: {
        isFormValid: false,
        rules: {
          nameRules: [
            v => !!v || this.text('non_empty_err'),
            v => v.length <= 255 || this.text('profile_max_len_err'),
          ],
        },
      },
      routerPrefix: '',
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    currentProject() {
      return this.$store.getters.projectCode;
    },
    profile() {
      return this.$store.getters['encodingProfiles/getProfile'];
    },
    profileDeleteRules() {
      const rules = [];
      const rule1 = v => !!v || this.text('non_empty_err');
      rules.push(rule1);
      if (this.profile.name) {
        const rule2 = v => (!!v && v) === this.profile.name || this.text('invalid_profile_name_err');
        rules.push(rule2);
      }
      return rules;
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  methods: {
    changeTab(tab) {
      this.currentTab = tab;
    },
    editProfile() {
      const profileId = this.$route.params.id;
      this.$router.push({
        name: `${this.routerPrefix}EncodingProfileEditPage`,
        params: {
          id: profileId,
        },
      });
    },
    deleteProfile() {
      this.$store.dispatch('encodingProfiles/deleteProfile', this.profile.id)
        .then(() => {
          this.$router.push({ name: `${this.routerPrefix}EncodingProfileListPage` });
        })
        .catch((errorMsg) => {
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 7000,
          });
        });
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
};
</script>
